import { useBoardTableRowDialog } from '@modules/board/BoardTableRowDialog';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Box, Stack, Typography } from '@mui/material';
import { type Editor } from '@tiptap/react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { CPAvatar } from 'src/components/base/CPAvatar';
import Iconify from 'src/components/iconify';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { IMention, type UserMentionType } from 'src/requests/mentions';
import { useMentionsMutation } from '../useMentionsMutation';
import {
  ChatMessageContentStyled,
  StyledMenuItem,
  StyledRemoveIcon,
} from './UserMentionItem.styles';
const formatDate = (value: string, locale: Locale) =>
  format(new Date(value), 'dd/MM/yyyy hh:mm a', { locale });

interface Props {
  mention: IMention;
  readed: boolean;
  editor: Editor;
  type: UserMentionType;
}

export const UserMentionItem = ({ mention, readed, editor, type }: Props) => {
  const navigate = useNavigate();
  const author = useWorkspaceUser(mention.author);
  const { readMention, removeMention } = useMentionsMutation();
  const { showBoardTableRowDialog } = useBoardTableRowDialog();
  const { currentLang } = useLocales();
  const handleGoToMessage = async () => {
    readMention(mention._id);
    if (type === 'rowMessage') {
      return showBoardTableRowDialog({
        viewId: '',
        boardId: mention?.boardId,
        tableId: mention?.tableId,
        rowId: mention?.rowId || '',
      });
    }
    navigate(PATH_DASHBOARD.chat.view(mention.channel._id), {
      state: { goToMessage: message._id },
    });
  };

  if (!author) return null;

  const message = useMemo(
    () => ({
      ...mention,
      channel: mention?.channel?._id,
      isSameAuthorAsPreviousMessage: false,
      isSameDayAsPreviousMessage: false,
    }),
    [mention],
  );

  const handleRemoveMention = (e: any) => {
    e.stopPropagation();
    removeMention(mention._id);
  };

  return (
    <StyledMenuItem
      key={mention._id}
      className={classNames({ readed })}
      onClick={handleGoToMessage}
    >
      <Stack direction="row" gap={1} width={1}>
        <Box>
          <UserProfileShortPopoverTrigger userId={author?._id}>
            <CPAvatar
              key={author._id}
              name={author.name}
              picture={author.picture || ''}
              status={author.status}
            />
          </UserProfileShortPopoverTrigger>
        </Box>
        <Stack gap={0.5} overflow="hidden" flexGrow={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            pt={0.5}
            flexGrow={1}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle1">{author.name}</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {mention.channel?.name}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" pt={0.5}>
              {formatDate(mention.createdAt, currentLang.dateValue)}
            </Typography>
          </Stack>
          {editor && (
            <ChatMessageContentStyled editor={editor} message={message} />
          )}
        </Stack>
      </Stack>
      <StyledRemoveIcon onClick={handleRemoveMention} size="small">
        <Iconify icon="ic:round-close" />
      </StyledRemoveIcon>
    </StyledMenuItem>
  );
};
